// #region IMPORTS
import { useEffect, useState } from 'react';
import { informUser, TOAST_TYPES } from '@azmed-org/azcomponents';

import {
  applyAppUpdate,
  startAppUpdateWatcher,
  subscribeToAppUpdate,
} from './appUpdateService';
// #endregion

const RELOAD_DELAY_MS = 2000;

/** Warns the user that the page is about to reload on its own. */
const notifyNewVersion = () =>
  informUser({
    title: 'New version available',
    message: 'Application will reload...',
    type: TOAST_TYPES.info,
    // Dedupes the toast if the hook ends up mounted by two consumers.
    toastId: 'app-update',
    // informUser defaults to 3000ms, so the delay has to be overridden here to
    // stay in step with the reload timer below.
    props: { duration: RELOAD_DELAY_MS },
  });

/**
 * Detects a newly deployed version, warns the user, then reloads the app.
 *
 * @returns {void}
 */
export const useAppUpdateWatcher = () => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToAppUpdate(() => setIsUpdateAvailable(true));
    // Started from an effect, never at module load, so the toast portal is
    // already mounted: a toast emitted before it is simply lost.
    startAppUpdateWatcher();

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isUpdateAvailable) return undefined;

    notifyNewVersion();
    const timeoutId = setTimeout(applyAppUpdate, RELOAD_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isUpdateAvailable]);
};
